
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';
import ErrorToast from '../components/ErrorToast';
import ClienteDetail from './ClienteDetail';
import MudarClienteModal from '../components/MudarClienteModal';
import { FaBuilding, FaMapMarkerAlt, FaTag, FaUser, FaHome, FaDollarSign, FaListOl } from 'react-icons/fa';
import './ImovelDetail.css';

const InfoCard = ({ title, icon, children, className = '' }) => (
  <div className={`info-card ${className}`}>
    <div className="info-card-header">
      {icon}
      <h2>{title}</h2>
    </div>
    <div className="info-card-body">
      {children}
    </div>
  </div>
);

const InfoRow = ({ label, value, isLink = false, to = '#' }) => {
  if (value == null || value === '') return null;
  const content = isLink ? <Link to={to} className="info-link">{String(value)}</Link> : String(value);
  return (
    <div className="info-row">
      <span className="info-label">{label}</span>
      <span className="info-value">{content}</span>
    </div>
  );
};

const ImovelDetail = () => {
  const { id } = useParams();
  const [imovel, setImovel] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [view, setView] = useState('overview'); // 'overview' or 'proprietario'

  const buscarImovel = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get(`/imoveis/${id}`);
      setImovel(data);
    } catch (e) {
      setErro('Erro ao buscar detalhes do imóvel.');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    buscarImovel();
  }, [buscarImovel]);

  const handleClienteChanged = () => {
    setModalAberto(false);
    buscarImovel();
  };

  if (carregando) return <Loading />;
  if (erro) return <ErrorToast message={erro} onClose={() => setErro('')} />;
  if (!imovel) return <p className="text-center">Imóvel não encontrado.</p>;

  const { proprietario_atual: prop } = imovel;

  if (view === 'proprietario' && prop && prop.id) {
    return (
      <div className="imovel-detail-container">
        <button onClick={() => setView('overview')} className="back-to-overview-btn">
          &larr; Voltar para o Imóvel
        </button>
        <ClienteDetail cliente={prop} />
      </div>
    );
  }

  return (
    <div className="imovel-detail-container">
      <header className="imovel-header">
        <div className="imovel-header-icon">
          <FaHome size={40} />
        </div>
        <div className="imovel-header-info">
          <h1>{`${imovel.logradouro}, ${imovel.numero}`}</h1>
          <p>{`${imovel.bairro}, ${imovel.cidade} - ${imovel.uf}`}</p>
        </div>
        <div className="imovel-header-meta">
          <span className={`status-tag status-${imovel.status?.toLowerCase()}`}>{imovel.status || 'Indefinido'}</span>
          <span>ID: {imovel.id}</span>
        </div>
      </header>

      <main className="imovel-main-content">
        <div className="imovel-col-1">
          <InfoCard title="Detalhes do Imóvel" icon={<FaHome />}>
            <InfoRow label="Tipo" value={imovel.tipo} />
            <InfoRow label="Nº Contribuinte" value={imovel.numero_contribuinte} />
            <InfoRow label="Referência" value={imovel.referencia} />
            <InfoRow label="Área Construída" value={imovel.area_construida ? `${imovel.area_construida} m²` : '-'} />
            <InfoRow label="Área do Terreno" value={imovel.area_terreno ? `${imovel.area_terreno} m²` : '-'} />
            <InfoRow label="Ano de Construção" value={imovel.ano_construcao} />
          </InfoCard>

          <InfoCard title="Endereço" icon={<FaMapMarkerAlt />}>
            <InfoRow label="Logradouro" value={imovel.logradouro} />
            <InfoRow label="Número" value={imovel.numero} />
            <InfoRow label="Complemento" value={imovel.complemento} />
            <InfoRow label="Bairro" value={imovel.bairro} />
            <InfoRow label="Cidade" value={imovel.cidade} />
            <InfoRow label="UF" value={imovel.uf} />
            <InfoRow label="CEP" value={imovel.cep} />
          </InfoCard>

          {imovel.condominio && (
            <InfoCard title="Condomínio" icon={<FaBuilding />}>
              <InfoRow label="Nome" value={imovel.condominio.nome} />
              <InfoRow label="CNPJ" value={imovel.condominio.cnpj} />
              <InfoRow label="Unidades" value={imovel.condominio.qtd_unidades} />
              <InfoRow label="Blocos" value={imovel.condominio.qtd_blocos} />
              <InfoRow label="Administradora" value={imovel.condominio.administradora} />
              <InfoRow label="Contato Adm." value={imovel.condominio.telefone_administradora || imovel.condominio.email_administradora} />
            </InfoCard>
          )}
        </div>

        <div className="imovel-col-2">
          <InfoCard title="Proprietário Atual" icon={<FaUser />} className="proprietario-card">
            {prop && prop.id ? (
              <div className="proprietario-details">
                <InfoRow label="Nome" value={prop.nome} />
                <InfoRow label="Documento" value={prop.documento} />
                <InfoRow label="Tipo" value={prop.tipo} />
                <div className="proprietario-actions">
                  <button onClick={() => setView('proprietario')} className="button-secondary">
                    Ver Detalhes
                  </button>
                  <button onClick={() => setModalAberto(true)} className="button-primary">
                    Mudar Proprietário
                  </button>
                </div>
              </div>
            ) : (
              <div className="proprietario-details">
                <p>Nenhum proprietário associado.</p>
                <div className="proprietario-actions">
                  <button onClick={() => setModalAberto(true)} className="button-primary">
                    Associar Proprietário
                  </button>
                </div>
              </div>
            )}
          </InfoCard>
          
          <InfoCard title="Valores" icon={<FaDollarSign />}>
            <InfoRow label="Valor de Venda" value={imovel.valor_venda ? `R$ ${imovel.valor_venda}`: 'Não informado'} />
            <InfoRow label="Valor de Locação" value={imovel.valor_locacao? `R$ ${imovel.valor_locacao}`: 'Não informado'} />
            <InfoRow label="IPTU" value={imovel.valor_iptu? `R$ ${imovel.valor_iptu}`: 'Não informado'} />
            <InfoRow label="Condomínio" value={imovel.valor_condominio? `R$ ${imovel.valor_condominio}`: 'Não informado'} />
          </InfoCard>

          {(() => {
            const etiquetas = imovel.etiquetas || (imovel.etiquetas ? [imovel.etiquetas] : []);
            return (
              <InfoCard title="Etiquetas" icon={<FaTag />}>
                {etiquetas.length > 0 ? (
                  <div className="etiquetas-list">
                    {etiquetas.map((et) => (
                      <span key={et.id || et.nome} className="etiqueta-chip">{et.nome || String(et)}</span>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: '#666' }}>Nenhuma etiqueta atribuída.</p>
                )}
              </InfoCard>
            );
          })()}

          {imovel.historico_proprietarios && imovel.historico_proprietarios.length > 0 && (
            <InfoCard title="Histórico de Proprietários" icon={<FaListOl />}>
              <div className="table-container">
                <table className="historico-table">
                  <thead>
                    <tr>
                      <th>Proprietário</th>
                      <th>Início</th>
                      <th>Fim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imovel.historico_proprietarios.map(h => (
                      <tr key={h.id}>
                        <td>{h.cliente?.nome || 'Informação indisponível'}</td>
                        <td>{new Date(h.inicio_vigencia).toLocaleDateString()}</td>
                        <td>{h.fim_vigencia ? new Date(h.fim_vigencia).toLocaleDateString() : 'Atual'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </InfoCard>
          )}
        </div>
      </main>

      {modalAberto && (
        <MudarClienteModal 
          imovelId={id} 
          onClose={() => setModalAberto(false)} 
          onClienteChanged={handleClienteChanged} 
        />
      )}
    </div>
  );
};

export default ImovelDetail;
